import React, { useMemo } from 'react';
import { Descriptions, Drawer, Empty, Flex, Tag, Timeline, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DeliveryEventDetail } from '../../../domain/entities/DeliveryDashboard';
import DeliveryOutcomeTag from './DeliveryOutcomeTag.component';

interface DeliveryDetailDrawerProps {
  detail?: DeliveryEventDetail;
  onClose: () => void;
}

const DeliveryDetailDrawer: React.FC<DeliveryDetailDrawerProps> = ({ detail, onClose }) => {
  const { t, i18n } = useTranslation();
  const dateFormat = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'medium' }), [i18n.language]);
  const format = (value?: string) => (value ? dateFormat.format(new Date(value)) : '—');
  const errorLabel = (code: string) => t(`delivery-error-${code}`, { ns: 'labels', defaultValue: code });
  const event = detail?.event;

  return (
    <Drawer open={Boolean(detail)} onClose={onClose} width={560} title={t('delivery-detail-title', { ns: 'titles' })} destroyOnClose>
      {event && (
        <Flex vertical gap={20}>
          <Flex gap={8} align="center" wrap="wrap">
            <DeliveryOutcomeTag outcome={event.outcome} />
            {event.errorCode && <Tag color="error">{errorLabel(event.errorCode)}</Tag>}
          </Flex>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('delivery-advert', { ns: 'labels' })}>
              {event.advertTitle
                ? <Link to={`/adverts/${event.advertUuid}`}>{event.advertTitle}</Link>
                : <Typography.Text type="secondary">{t('delivery-deleted', { ns: 'labels' })}</Typography.Text>}
            </Descriptions.Item>
            <Descriptions.Item label={t('channel-link', { ns: 'labels' })}>
              {event.channelLink
                ? <a href={event.channelLink} target="_blank" rel="noreferrer">{event.channelLink}</a>
                : <Typography.Text type="secondary">{t('delivery-deleted', { ns: 'labels' })}</Typography.Text>}
            </Descriptions.Item>
            <Descriptions.Item label={t('delivery-account', { ns: 'labels' })}>{event.telegramAccountPhone || event.telegramAccountUuid}</Descriptions.Item>
            <Descriptions.Item label={t('delivery-client', { ns: 'labels' })}>{event.clientEmail || `#${event.accountId}`}</Descriptions.Item>
            <Descriptions.Item label={t('delivery-planned-at', { ns: 'labels' })}>{format(event.plannedAt)}</Descriptions.Item>
            <Descriptions.Item label={t('delivery-finalized-at', { ns: 'labels' })}>{format(event.finalizedAt)}</Descriptions.Item>
            {event.nextAttemptAt && (
              <Descriptions.Item label={t('delivery-next-attempt', { ns: 'labels' })}>{format(event.nextAttemptAt)}</Descriptions.Item>
            )}
            <Descriptions.Item label={t('delivery-attempts', { ns: 'labels' })}>
              {event.maxAttempts > 0 ? `${event.attempt} / ${event.maxAttempts}` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('delivery-schedule', { ns: 'labels' })}>
              <Typography.Text copyable className="delivery-detail-uuid">{event.sendingScheduleUuid}</Typography.Text>
            </Descriptions.Item>
          </Descriptions>

          <div>
            <Typography.Title level={5}>{t('delivery-attempts-history', { ns: 'titles' })}</Typography.Title>
            {detail.attempts.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('delivery-no-attempt-errors', { ns: 'labels' })} />
            ) : (
              <Timeline
                items={detail.attempts.map((attempt) => ({
                  color: 'red',
                  children: (
                    <Flex vertical gap={4}>
                      <Typography.Text strong>
                        {t('delivery-attempt-number', { ns: 'labels', number: attempt.attempt })} · {format(attempt.at)}
                      </Typography.Text>
                      {attempt.errorCode && <Tag color="error" className="delivery-detail-code">{errorLabel(attempt.errorCode)}</Tag>}
                      <Typography.Paragraph code copyable className="delivery-detail-error">{attempt.error}</Typography.Paragraph>
                    </Flex>
                  ),
                }))}
              />
            )}
          </div>
        </Flex>
      )}
    </Drawer>
  );
};

export default DeliveryDetailDrawer;
